import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Chip,
  Stack,
  TextField
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CategoryIcon from '@mui/icons-material/Category';
import LanguageIcon from '@mui/icons-material/Language';
import PublicIcon from '@mui/icons-material/Public';
import TokenIcon from '@mui/icons-material/Token';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import PieChartIcon from '@mui/icons-material/PieChart';
import SpeedIcon from '@mui/icons-material/Speed';
import { dsVar, ink } from '../theme/tokens';
import { useThemeMode, useThemeTokens } from '../theme/ThemeModeProvider';

/**
 * Share donuts (languages, geography): one hue, light to dark. On white the
 * pale yellows vanish, so the light ramp starts at a gold that clears the
 * surface and keeps every step visibly apart.
 */
const AMBER_RAMP_DARK = ['#FFDE59', '#FACC15', '#EAB308', '#CA8A04', '#A16207'];
const AMBER_RAMP_LIGHT = ['#CA8A04', '#A16207', '#7C4A03', '#4A2C02', '#2A1801'];

const AnalyticsVisualizations = ({ data, onIntervalChange, onDateRangeChange = () => {} }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // d3 writes most colours as SVG attributes, where CSS variables do not resolve.
  const tokens = useThemeTokens();
  const light = useThemeMode().resolved === 'light';
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMetric, setSelectedMetric] = useState('total_requests');
  const [startDate, setStartDate] = useState(data.summary?.date_range?.start);
  const [endDate, setEndDate] = useState(data.summary?.date_range?.end);

  // Refs for charts
  const timeSeriesRef = useRef(null);
  const statusDistributionRef = useRef(null);
  const languagesRef = useRef(null);
  const geographyRef = useRef(null);
  const executionTimeRef = useRef(null);

  // Metrics options
  const metricsOptions = [
    { value: 'total_requests', label: 'Total Requests' },
    { value: 'successful_requests', label: 'Successful Requests' },
    { value: 'failed_requests', label: 'Failed Requests' },
    { value: 'avg_execution_time', label: 'Average Execution Time' }
  ];

  // Time Series Chart
  useEffect(() => {
    if (!data || !timeSeriesRef.current) return;

    const margin = { top: 20, right: 30, bottom: 40, left: 60 };
    const width = timeSeriesRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    // Clear previous chart
    d3.select(timeSeriesRef.current).selectAll("*").remove();

    const svg = d3.select(timeSeriesRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Parse dates
    const parseDate = d3.timeParse("%Y-%m-%d");
    const timeData = data.data.map(d => ({
      date: parseDate(d.date),
      value: selectedMetric === 'avg_execution_time' 
        ? parseFloat(d?.metrics?.[selectedMetric] || 0)
        : +d?.metrics?.[selectedMetric] || 0
    }));

    // X scale
    const x = d3.scaleTime()
      .domain(d3.extent(timeData, d => d.date))
      .range([0, width]);

    // Y scale
    const y = d3.scaleLinear()
      .domain([0, d3.max(timeData, d => d.value) * 1.1])
      .range([height, 0]);

    // Add gradient
    const gradient = svg.append("defs")
      .append("linearGradient")
      .attr("id", "time-gradient")
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", 0)
      .attr("y1", y(0))
      .attr("x2", 0)
      .attr("y2", y(d3.max(timeData, d => d.value)))
      .selectAll("stop")
      .data([
        { offset: "0%", color: tokens['accent-fg'], opacity: 1 },
        { offset: "100%", color: tokens['accent-fg'], opacity: 0.2 }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color)
      .attr("stop-opacity", d => d.opacity);

    // Add area
    const area = d3.area()
      .x(d => x(d.date))
      .y0(height)
      .y1(d => y(d.value))
      .curve(d3.curveMonotoneX);

    svg.append("path")
      .datum(timeData)
      .attr("fill", "url(#time-gradient)")
      .attr("opacity", 0.1)
      .attr("d", area);

    // Add the line
    const line = d3.line()
      .x(d => x(d.date))
      .y(d => y(d.value))
      .curve(d3.curveMonotoneX);

    svg.append("path")
      .datum(timeData)
      .attr("fill", "none")
      .attr("stroke", tokens['accent-fg'])
      .attr("stroke-width", 2)
      .attr("d", line);

    // Add dots with hover effect
    const dots = svg.selectAll("dot")
      .data(timeData)
      .enter()
      .append("circle")
      .attr("cx", d => x(d.date))
      .attr("cy", d => y(d.value))
      .attr("r", 4)
      .style("fill", tokens['accent-fg'])
      .style("opacity", 0.8)
      .style("cursor", "pointer");

    // Add X axis
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5))
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)")
      .style("fill", tokens['fg-muted'])
      .style("font-size", "12px");

    // Add Y axis
    svg.append("g")
      .call(d3.axisLeft(y).ticks(5)
        .tickFormat(d => {
          if (selectedMetric === 'avg_execution_time') {
            return d?.toFixed(2);
          }
          return d.toLocaleString();
        }))
      .selectAll("text")
      .style("fill", tokens['fg-muted'])
      .style("font-size", "12px");

    // Add hover effects
    const tooltip = d3.select(timeSeriesRef.current)
      .append("div")
      .style("opacity", 0)
      .attr("class", "tooltip")
      .style("background-color", tokens.surface)
      .style("border", `1px solid ${tokens.line}`)
      .style("border-radius", "8px")
      .style("padding", "12px")
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("box-shadow", "0 4px 12px rgba(0,0,0,0.1)")
      .style("z-index", "1000");

    dots.on("mouseover", function(event, d) {
      d3.select(this)
        .transition()
        .duration(200)
        .attr("r", 6)
        .style("fill", tokens['accent-fg']);
      
      tooltip.transition()
        .duration(200)
        .style("opacity", .9);
      
      tooltip.html(`
        <div style="font-size: 14px; color: rgb(var(--ds-fg-body)); font-weight: 600; margin-bottom: 4px;">
          ${d3.timeFormat("%B %d, %Y")(d.date)}
        </div>
        <div style="font-size: 13px; color: rgb(var(--ds-fg-muted));">
          ${metricsOptions.find(m => m.value === selectedMetric).label}: ${
            selectedMetric === 'avg_execution_time' 
              ? d.value.toFixed(2) + 's'
              : d.value.toLocaleString()
          }
        </div>
      `)
        .style("left", (event.pageX + 10) + "px")
        .style("top", (event.pageY - 28) + "px");
    })
    .on("mouseout", function() {
      d3.select(this)
        .transition()
        .duration(200)
        .attr("r", 4)
        .style("fill", tokens['accent-fg']);
      
      tooltip.transition()
        .duration(500)
        .style("opacity", 0);
    });

  }, [data, selectedMetric, tokens]);

  // Status Distribution Chart
  useEffect(() => {
    if (!data || !statusDistributionRef.current) return;

    const margin = { top: 20, right: 50, bottom: 100, left: 80 };
    const width = statusDistributionRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    // Clear previous chart
    d3.select(statusDistributionRef.current).selectAll("*").remove();

    const svg = d3.select(statusDistributionRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Calculate status distribution from the distributions.status
    const statusData = data.data.reduce((acc, curr) => {
      const statusDist = curr.distributions.status;
      Object.entries(statusDist).forEach(([status, count]) => {
        if (status === 'Success') {
          acc.success += count;
        } else {
          acc.failed += count;
        }
      });
      return acc;
    }, { success: 0, failed: 0 });

    const total = statusData.success + statusData.failed;
    const successRate = total > 0 ? ((statusData?.success / total) * 100)?.toFixed(1) : 0;

    const barData = [
      { label: "Successful", value: statusData.success, color: light ? "#16A34A" : "#4ade80" },
      { label: "Failed", value: statusData.failed, color: light ? "#B91C1C" : "#f87171" }
    ];

    // If no data, show empty state
    if (total === 0) {
      svg.append("text")
        .attr("text-anchor", "middle")
        .attr("dy", "0.35em")
        .style("fill", tokens['fg-muted'])
        .style("font-size", "14px")
        .text("No data available");
      return;
    }

    // X scale
    const x = d3.scaleLinear()
      .domain([0, d3.max(barData, d => d.value) * 1.1])
      .range([0, width]);

    // Y scale
    const y = d3.scaleBand()
      .domain(barData.map(d => d.label))
      .range([0, height])
      .padding(0.3);

    // Add bars
    svg.selectAll("rect")
      .data(barData)
      .enter()
      .append("rect")
      .attr("x", 0)
      .attr("y", d => y(d.label))
      .attr("width", d => x(d.value))
      .attr("height", y.bandwidth())
      .attr("rx", 4)
      .style("fill", d => d.color)
      .style("opacity", 0.9)
      .style("cursor", "pointer")
      .on("mouseover", function() {
        d3.select(this)
          .transition()
          .duration(200)
          .style("opacity", 1);
      })
      .on("mouseout", function() {
        d3.select(this)
          .transition()
          .duration(200)
          .style("opacity", 0.9);
      });

    // Add value labels
    svg.selectAll("text.value")
      .data(barData)
      .enter()
      .append("text")
      .attr("class", "value")
      .attr("x", d => x(d.value) + 10)
      .attr("y", d => y(d.label) + y.bandwidth()/2)
      .attr("dy", "0.35em")
      .style("fill", tokens['fg-muted'])
      .style("font-size", "13px")
      .style("font-weight", "500")
      .text(d => d.value.toLocaleString());

    // Add Y axis
    svg.append("g")
      .call(d3.axisLeft(y))
      .selectAll("text")
      .style("fill", tokens['fg-muted'])
      .style("font-size", "13px")
      .style("font-weight", "500");

    // Add X axis
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5))
      .selectAll("text")
      .style("fill", tokens['fg-muted'])
      .style("font-size", "12px");

    // Add success rate text
    svg.append("text")
      .attr("x", width/2)
      .attr("y", -10)
      .attr("text-anchor", "middle")
      .style("fill", tokens['fg-body'])
      .style("font-size", "14px")
      .style("font-weight", "600")
      .text(`Success Rate: ${successRate}%`);

  }, [data, tokens, light]);

  // Languages Distribution Chart
  useEffect(() => {
    if (!data || !languagesRef.current) return;

    const margin = { top: 10, right: 120, bottom: 100, left: 20 };
    const width = languagesRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;
    const radius = Math.min(width, height) / 2 - 40;

    // Clear previous chart
    d3.select(languagesRef.current).selectAll("*").remove();

    const svg = d3.select(languagesRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${width/2 + margin.left},${height/2 + margin.top})`);

    // Calculate language distribution
    const languageData = data.data.reduce((acc, curr) => {
      const langDist = curr.distributions.languages;
      Object.entries(langDist).forEach(([lang, count]) => {
        if (!acc[lang]) acc[lang] = 0;
        acc[lang] += count;
      });
      return acc;
    }, {});

    const pieData = Object.entries(languageData)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);

    // If no data, show empty state
    if (pieData.length === 0) {
      svg.append("text")
        .attr("text-anchor", "middle")
        .attr("dy", "0.35em")
        .style("fill", tokens['fg-muted'])
        .style("font-size", "14px")
        .text("No data available");
      return;
    }

    const color = d3.scaleOrdinal()
      .domain(pieData.map(d => d.label))
      .range(light ? AMBER_RAMP_LIGHT : AMBER_RAMP_DARK);

    const pie = d3.pie()
      .value(d => d.value)
      .sort(null);

    const arc = d3.arc()
      .innerRadius(radius * 0.6)
      .outerRadius(radius);

    const arcs = pie(pieData);

    // Add pie chart
    const g = svg.append("g");

    // Add segments
    g.selectAll("path")
      .data(arcs)
      .enter()
      .append("path")
      .attr("d", arc)
      .attr("fill", d => color(d.data.label))
      .attr("stroke", "white")
      .style("stroke-width", pieData.length === 1 ? "0px" : "2px")
      .style("opacity", 0.9)
      .style("cursor", "pointer")
      .on("mouseover", function() {
        d3.select(this)
          .transition()
          .duration(200)
          .style("opacity", 1);
      })
      .on("mouseout", function() {
        d3.select(this)
          .transition()
          .duration(200)
          .style("opacity", 0.9);
      });

    // Add center text with total
    const total = d3.sum(pieData, d => d.value);
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .style("fill", tokens['fg-body'])
      .style("font-size", "24px")
      .style("font-weight", "600")
      .text(total.toLocaleString());

    // Add legend
    const legend = svg.append("g")
      .attr("transform", `translate(${radius + 30}, ${-radius/2})`);

    pieData.forEach((d, i) => {
      const legendRow = legend.append("g")
        .attr("transform", `translate(0, ${i * 25})`);

      legendRow.append("rect")
        .attr("width", 12)
        .attr("height", 12)
        .attr("rx", 2)
        .style("fill", color(d.label));

      legendRow.append("text")
        .attr("x", 20)
        .attr("y", 10)
        .style("fill", tokens['fg-muted'])
        .style("font-size", "13px")
        .style("font-weight", "500")
        .text(`${d.label}: ${d.value.toLocaleString()}`);
    });

  }, [data, tokens, light]);

  // Geography Distribution Chart
  useEffect(() => {
    if (!data || !geographyRef.current) return;

    const margin = { top: 10, right: 120, bottom: 100, left: 20 };
    const width = geographyRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;
    const radius = Math.min(width, height) / 2 - 40;

    // Clear previous chart
    d3.select(geographyRef.current).selectAll("*").remove();

    const svg = d3.select(geographyRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${width/2 + margin.left},${height/2 + margin.top})`);

    // Calculate geography distribution
    const geoData = data.data.reduce((acc, curr) => {
      const geoDist = curr.distributions.geographies;
      Object.entries(geoDist).forEach(([geo, count]) => {
        if (!acc[geo]) acc[geo] = 0;
        acc[geo] += count;
      });
      return acc;
    }, {});

    const pieData = Object.entries(geoData)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);

    // If no data, show empty state
    if (pieData.length === 0) {
      svg.append("text")
        .attr("text-anchor", "middle")
        .attr("dy", "0.35em")
        .style("fill", tokens['fg-muted'])
        .style("font-size", "14px")
        .text("No data available");
      return;
    }

    const color = d3.scaleOrdinal()
      .domain(pieData.map(d => d.label))
      .range(light ? AMBER_RAMP_LIGHT : AMBER_RAMP_DARK);

    const pie = d3.pie()
      .value(d => d.value)
      .sort(null);

    const arc = d3.arc()
      .innerRadius(radius * 0.6)
      .outerRadius(radius);

    const arcs = pie(pieData);

    // Add pie chart
    const g = svg.append("g");

    // Add segments
    g.selectAll("path")
      .data(arcs)
      .enter()
      .append("path")
      .attr("d", arc)
      .attr("fill", d => color(d.data.label))
      .attr("stroke", "white")
      .style("stroke-width", pieData.length === 1 ? "0px" : "2px")
      .style("opacity", 0.9)
      .style("cursor", "pointer")
      .on("mouseover", function() {
        d3.select(this)
          .transition()
          .duration(200)
          .style("opacity", 1);
      })
      .on("mouseout", function() {
        d3.select(this)
          .transition()
          .duration(200)
          .style("opacity", 0.9);
      });

    // Add center text with total
    const total = d3.sum(pieData, d => d.value);
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .style("fill", tokens['fg-body'])
      .style("font-size", "24px")
      .style("font-weight", "600")
      .text(total.toLocaleString());

    // Add legend
    const legend = svg.append("g")
      .attr("transform", `translate(${radius + 30}, ${-radius/2})`);

    pieData.forEach((d, i) => {
      const legendRow = legend.append("g")
        .attr("transform", `translate(0, ${i * 25})`);

      legendRow.append("rect")
        .attr("width", 12)
        .attr("height", 12)
        .attr("rx", 2)
        .style("fill", color(d.label));

      legendRow.append("text")
        .attr("x", 20)
        .attr("y", 10)
        .style("fill", tokens['fg-muted'])
        .style("font-size", "13px")
        .style("font-weight", "500")
        .text(`${d.label}: ${d.value.toLocaleString()}`);
    });

  }, [data, tokens, light]);

  // Execution Time Chart
  useEffect(() => {
    if (!data || !executionTimeRef.current) return;

    const margin = { top: 20, right: 30, bottom: 40, left: 60 };
    const width = executionTimeRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    // Clear previous chart
    d3.select(executionTimeRef.current).selectAll("*").remove();

    const svg = d3.select(executionTimeRef.current)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Parse dates and execution times
    const parseDate = d3.timeParse("%Y-%m-%d");
    const executionData = data.data.map(d => ({
      date: parseDate(d.date),
      executionTime: parseFloat(d?.metrics?.avg_execution_time || 0)
    }));

    // Calculate statistics
    const avgExecutionTime = d3.mean(executionData, d => d.executionTime);
    const maxExecutionTime = d3.max(executionData, d => d.executionTime);
    const minExecutionTime = d3.min(executionData, d => d.executionTime);

    // X scale
    const x = d3.scaleTime()
      .domain(d3.extent(executionData, d => d.date))
      .range([0, width]);

    // Y scale
    const y = d3.scaleLinear()
      .domain([0, maxExecutionTime * 1.1])
      .range([height, 0]);

    // Add gradient
    const gradient = svg.append("defs")
      .append("linearGradient")
      .attr("id", "execution-gradient")
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", 0)
      .attr("y1", y(0))
      .attr("x2", 0)
      .attr("y2", y(maxExecutionTime))
      .selectAll("stop")
      .data([
        { offset: "0%", color: tokens['accent-fg'], opacity: 1 },
        { offset: "100%", color: tokens['accent-fg'], opacity: 0.2 }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color)
      .attr("stop-opacity", d => d.opacity);

    // Add area
    const area = d3.area()
      .x(d => x(d.date))
      .y0(height)
      .y1(d => y(d.executionTime))
      .curve(d3.curveMonotoneX);

    svg.append("path")
      .datum(executionData)
      .attr("fill", "url(#execution-gradient)")
      .attr("opacity", 0.1)
      .attr("d", area);

    // Add the line
    const line = d3.line()
      .x(d => x(d.date))
      .y(d => y(d.executionTime))
      .curve(d3.curveMonotoneX);

    svg.append("path")
      .datum(executionData)
      .attr("fill", "none")
      .attr("stroke", tokens['accent-fg'])
      .attr("stroke-width", 2)
      .attr("d", line);

    // Add dots with hover effect
    const dots = svg.selectAll("dot")
      .data(executionData)
      .enter()
      .append("circle")
      .attr("cx", d => x(d.date))
      .attr("cy", d => y(d.executionTime))
      .attr("r", 4)
      .style("fill", tokens['accent-fg'])
      .style("opacity", 0.8)
      .style("cursor", "pointer");

    // Add X axis
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5))
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)")
      .style("fill", tokens['fg-muted'])
      .style("font-size", "12px");

    // Add Y axis
    svg.append("g")
      .call(d3.axisLeft(y).ticks(5)
        .tickFormat(d => d?.toFixed(2)))
      .selectAll("text")
      .style("fill", tokens['fg-muted'])
      .style("font-size", "12px");

    // Add hover effects
    const tooltip = d3.select(executionTimeRef.current)
      .append("div")
      .style("opacity", 0)
      .attr("class", "tooltip")
      .style("background-color", tokens.surface)
      .style("border", `1px solid ${tokens.line}`)
      .style("border-radius", "8px")
      .style("padding", "12px")
      .style("position", "absolute")
      .style("pointer-events", "none")
      .style("box-shadow", "0 4px 12px rgba(0,0,0,0.1)")
      .style("z-index", "1000");

    dots.on("mouseover", function(event, d) {
      d3.select(this)
        .transition()
        .duration(200)
        .attr("r", 6)
        .style("fill", tokens['accent-fg']);
      
      tooltip.transition()
        .duration(200)
        .style("opacity", .9);
      
      tooltip.html(`
        <div style="font-size: 14px; color: rgb(var(--ds-fg-body)); font-weight: 600; margin-bottom: 4px;">
          ${d3.timeFormat("%B %d, %Y")(d.date)}
        </div>
        <div style="font-size: 13px; color: rgb(var(--ds-fg-muted));">
          Execution Time: ${d?.executionTime?.toFixed(2)}s
        </div>
      `)
        .style("left", (event.pageX + 10) + "px")
        .style("top", (event.pageY - 28) + "px");
    })
    .on("mouseout", function() {
      d3.select(this)
        .transition()
        .duration(200)
        .attr("r", 4)
        .style("fill", tokens['accent-fg']);
      
      tooltip.transition()
        .duration(500)
        .style("opacity", 0);
    });

    // Add performance stats box
    const statsBox = svg.append("g")
      .attr("transform", `translate(${width - 150}, 10)`);

    statsBox.append("rect")
      .attr("width", 140)
      .attr("height", 80)
      .attr("rx", 8)
      .style("fill", tokens.surface)
      .style("stroke", tokens.line)
      .style("stroke-width", 1);

    statsBox.append("text")
      .attr("x", 10)
      .attr("y", 25)
      .style("fill", tokens['fg-body'])
      .style("font-size", "12px")
      .style("font-weight", "600")
      .text("Performance Stats");

    statsBox.append("text")
      .attr("x", 10)
      .attr("y", 45)
      .style("fill", tokens['fg-muted'])
      .style("font-size", "11px")
      .text(`Avg: ${avgExecutionTime?.toFixed(2)}s`);

    statsBox.append("text")
      .attr("x", 10)
      .attr("y", 65)
      .style("fill", tokens['fg-muted'])
      .style("font-size", "11px")
      .text(`Max: ${maxExecutionTime?.toFixed(2)}s`);

  }, [data, tokens]);

  // Handle date range changes
  const handleDateRangeChange = (newStartDate, newEndDate) => {
    if (newStartDate && newEndDate) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      // Trigger API call immediately
      onDateRangeChange({
        startDate: newStartDate,
        endDate: newEndDate
      });
    }
  };

  // Handle start date change
  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    if (newStartDate && newStartDate <= endDate) {
      handleDateRangeChange(newStartDate, endDate);
    }
  };

  // Handle end date change
  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    if (newEndDate && newEndDate >= startDate) {
      handleDateRangeChange(startDate, newEndDate);
    }
  };

  // Calculate success rate
  const calculateSuccessRate = () => {
    if (!data?.data?.[0]?.distributions?.status) return 0;
    const statusData = data.data[0].distributions.status;
    const total = Object.values(statusData).reduce((a, b) => a + b, 0);
    const success = statusData['Success'] || 0;
    return ((success / total) * 100)?.toFixed(1);
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header and Filters */}
      <Box sx={{ 
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 2
      }}>
        <div className="mb-10 flex flex-col gap-4 m-0 text-left items-start">
          <h1 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-fg m-0 mr-auto p-0 font-mono">
              Activity <span className="text-fg font-normal m-0" style={{ fontFamily: 'Inter, sans-serif' }}>Monitor</span>
          </h1>
          <p className="text-fg-2 text-lg max-w-2xl font-light leading-relaxed m-0 p-0 tracking-normal font-sans">
              Track and manage your agent executions in real-time to ensure optimal performance and reliability.
          </p>
        </div>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          width: { xs: '100%', md: 'auto' }
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            backgroundColor: dsVar('surface'),
            padding: '8px 16px',
            borderRadius: '8px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none'
          }}>
                            <CalendarMonthIcon sx={{ color: dsVar('accent-fg') }} />
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: endDate }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '&:hover fieldset': {
                    borderColor: dsVar('accent-fg'),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: dsVar('accent-fg'),
                  }
                }
              }}
            />
            <Typography sx={{ color: dsVar('fg-muted') }}>to</Typography>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '&:hover fieldset': {
                    borderColor: dsVar('accent-fg'),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: dsVar('accent-fg'),
                  }
                }
              }}
            />
          </Box>
          <Box sx={{ 
            backgroundColor: dsVar('surface'),
            padding: '8px 16px',
            borderRadius: '8px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none'
          }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={data.interval}
                onChange={(e) => onIntervalChange(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: dsVar('line'),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: dsVar('accent-fg'),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: dsVar('accent-fg'),
                  },
                  '& .MuiSelect-select': {
                    color: dsVar('fg-body'),
                    fontWeight: 500
                  }
                }}
              >
                <MenuItem value="day">Daily</MenuItem>
                {/* <MenuItem value="week">Weekly</MenuItem> */}
                <MenuItem value="month">Monthly</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Average Execution Time */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: '12px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: '1px solid rgba(255, 222, 89, 0.3)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 222, 89, 0.1)',
                  borderRadius: '8px',
                  p: 1,
                  mr: 2
                }}>
                  <TimerIcon sx={{ color: dsVar('accent-fg') }} />
                </Box>
                <Typography sx={{ 
                  color: dsVar('fg-muted'),
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  Avg Execution Time
                </Typography>
              </Box>
              <Typography sx={{ 
                color: dsVar('fg-body'),
                fontSize: '24px',
                fontWeight: 600
              }}>
                {data.data[0]?.metrics?.avg_execution_time?.toFixed(2)}s
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Success Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: '12px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: '1px solid rgba(255, 222, 89, 0.3)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(74, 222, 128, 0.1)',
                  borderRadius: '8px',
                  p: 1,
                  mr: 2
                }}>
                  <CheckCircleIcon sx={{ color: ink('#4ade80') }} />
                </Box>
                <Typography sx={{ 
                  color: dsVar('fg-muted'),
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  Success Rate
                </Typography>
              </Box>
              <Typography sx={{ 
                color: dsVar('fg-body'),
                fontSize: '24px',
                fontWeight: 600
              }}>
                {calculateSuccessRate()}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Input Tokens */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: '12px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: '1px solid rgba(255, 222, 89, 0.3)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(251, 146, 60, 0.1)',
                  borderRadius: '8px',
                  p: 1,
                  mr: 2
                }}>
                  <InputIcon sx={{ color: ink('#fb923c') }} />
                </Box>
                <Typography sx={{ 
                  color: dsVar('fg-muted'),
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  Input Tokens
                </Typography>
              </Box>
              <Typography sx={{ 
                color: dsVar('fg-body'),
                fontSize: '24px',
                fontWeight: 600
              }}>
                {data.summary.total_input_tokens.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Output Tokens */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: '12px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: '1px solid rgba(255, 222, 89, 0.3)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(96, 165, 250, 0.1)',
                  borderRadius: '8px',
                  p: 1,
                  mr: 2
                }}>
                  <OutputIcon sx={{ color: ink('#60a5fa') }} />
                </Box>
                <Typography sx={{ 
                  color: dsVar('fg-muted'),
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  Output Tokens
                </Typography>
              </Box>
              <Typography sx={{ 
                color: dsVar('fg-body'),
                fontSize: '24px',
                fontWeight: 600
              }}>
                {data.summary.total_output_tokens.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Time Series Chart */}
        <Grid item xs={6}>
          <Card sx={{ 
            borderRadius: '12px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: '1px solid rgba(255, 222, 89, 0.3)',
            }
          }}>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 3
              }}>
                <Typography variant="h6" sx={{ 
                  color: dsVar('fg-body'),
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <TimelineIcon sx={{ color: dsVar('accent-fg') }} />
                  Request Trends
                </Typography>
                {/* <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    sx={{
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: dsVar('line'),
                      },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: dsVar('accent-fg'),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: dsVar('accent-fg'),
                  }
                    }}
                  >
                    {metricsOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl> */}
              </Box>
              <Box ref={timeSeriesRef} sx={{ height: 300 }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Execution Time Chart */}
        <Grid item xs={6}>
          <Card sx={{ 
            borderRadius: '12px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: '1px solid rgba(255, 222, 89, 0.3)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ 
                color: dsVar('fg-body'),
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3
              }}>
                <SpeedIcon sx={{ color: dsVar('accent-fg') }} />
                Execution Time Analysis
              </Typography>
              <Box ref={executionTimeRef} sx={{ height: 300 }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Status Distribution Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: '12px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: '1px solid rgba(255, 222, 89, 0.3)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ 
                color: dsVar('fg-body'),
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3
              }}>
                <PieChartIcon sx={{ color: dsVar('accent-fg') }} />
                Status Distribution
              </Typography>
              <Box ref={statusDistributionRef} sx={{ height: 200 }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Languages Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: '12px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: '1px solid rgba(255, 222, 89, 0.3)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ 
                color: dsVar('fg-body'),
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3
              }}>
                <LanguageIcon sx={{ color: dsVar('accent-fg') }} />
                Languages Distribution
              </Typography>
              <Box ref={languagesRef} sx={{ height: 200 }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Geographies Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: '12px',
            backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              border: '1px solid rgba(255, 222, 89, 0.3)',
            }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ 
                color: dsVar('fg-body'),
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3
              }}>
                <PublicIcon sx={{ color: dsVar('accent-fg') }} />
                Geographies Distribution
              </Typography>
              <Box ref={geographyRef} sx={{ height: 200 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsVisualizations; 